import React, { useState } from "react";
import { sendMessage } from "../../../api/api";
import "./Careers.css";

function Careers() {
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    why: "",
    experience: "",
    handleConflict: "",
    fairTeams: "",
    gamesPerMonth: "",
    coachingRefExp: "",
    availability: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await sendMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: "Game Captain Application",
        message:
          `Why are you interested? ${formData.why}\n` +
          `Phone: ${formData.phone}\n` +
          `How would you handle conflict? ${formData.handleConflict}\n` +
          `How would you ensure fair teams? ${formData.fairTeams}\n` +
          `Games per month: ${formData.gamesPerMonth}\n` +
          `Coaching/Refereeing experience: ${formData.coachingRefExp}\n` +
          `Relevant Experience: ${formData.experience}\n` +
          `Availability: ${formData.availability}`,
        timestamp: new Date().toISOString(),
        type: "job_application",
        job: "Game Captain",
      });
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        why: "",
        experience: "",
        handleConflict: "",
        fairTeams: "",
        gamesPerMonth: "",
        coachingRefExp: "",
        availability: "",
      });
      setShowApplyForm(false);
      // Scroll to top after successful submit
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show submitted page if application was successful
  if (submitStatus === "success") {
    return (
      <div className="careers-page" style={{ padding: '2rem', minHeight: '60vh', textAlign: 'center' }}>
        <h1>Application Submitted!</h1>
        <p style={{ fontSize: '1.2rem', color: '#059669', margin: '2rem 0' }}>
          Thank you for applying to be a Game Captain. We'll review your application and get back to you soon.
        </p>
        <button
          style={{ background: '#10b981', color: '#fff', fontWeight: 600, border: 'none', borderRadius: '8px', padding: '0.8rem 2rem', fontSize: '1rem', cursor: 'pointer' }}
          onClick={() => { setSubmitStatus(null); setShowJobDetails(false); }}
        >
          Back to Careers
        </button>
      </div>
    );
  }

  return (
    <div className="careers-page" style={{ padding: '2rem', minHeight: '60vh' }}>
      <h1>Careers at GoalFlex</h1>
      <p>
        We’re always looking for passionate people to join our team! If you’re interested in helping us connect soccer players and build inclusive communities, check back soon for open positions or email us at <a href="mailto:support@goalflex.net">support@goalflex.net</a>.
      </p>
      <h2>Why Work With Us?</h2>
      <ul>
        <li>Inclusive, player-first culture</li>
        <li>Remote-friendly and flexible hours</li>
        <li>Opportunities to grow with a fast-moving startup</li>
      </ul>
      <h2>Open Positions</h2>
      <div className={`job-opening${!showJobDetails ? ' collapsed' : ''}`}>
        <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Game Captain
          <button
            className="read-more-btn"
            style={{ fontSize: '1rem', padding: '0.4rem 1.2rem', borderRadius: '6px', border: 'none', background: '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer', marginLeft: '1rem' }}
            onClick={() => setShowJobDetails((prev) => !prev)}
            aria-expanded={showJobDetails}
          >
            {showJobDetails ? 'Collapse' : 'Read More'}
          </button>
        </h3>
        {/* Short summary always visible */}
        <div className="job-summary" style={{ marginBottom: showJobDetails ? '0' : '1.5rem', color: '#334155' }}>
          <strong>Role:</strong> Lead and organize pickup soccer games.<br />
          <strong>Type:</strong> Part-time, volunteer, flexible schedule<br />
          <strong>Location:</strong> Minnesota (various fields)
        </div>
        {showJobDetails && (
          <div className="job-details">
            <h4>Overview</h4>
            <p>
              Game Captains play a key role in making sure every pickup soccer game runs smoothly. They are responsible for leading on game day, keeping things organized, and ensuring players have a fun and safe experience.
            </p>
            <hr style={{ margin: '2rem 0' }} />
            <h4>Responsibilities</h4>
            <strong>Before the Game</strong>
            <ul>
              <li>Confirm attendance by reminding players of the game time.</li>
              <li>Bring needed gear (ball, cones, pinnies if provided).</li>
              <li>Arrive 10–15 minutes early to prepare.</li>
            </ul>
            <strong>During the Game</strong>
            <ul>
              <li>Organize fair and balanced teams.</li>
              <li>Start the game on time.</li>
              <li>Keep order, encourage fair play, and handle small conflicts.</li>
              <li>Watch for safety and step in if play becomes unsafe.</li>
            </ul>
            <strong>After the Game</strong>
            <ul>
              <li>Wrap up on time and thank the players.</li>
              <li>Collect equipment (balls, cones, pinnies).</li>
              <li>Make sure the field is left clean.</li>
            </ul>
            <hr style={{ margin: '2rem 0' }} />
            <h4>Qualifications</h4>
            <strong>Required</strong>
            <ul>
              <li>Reliable and punctual.</li>
              <li>Good communication skills.</li>
              <li>Team-minded, with fairness in organizing games.</li>
              <li>Calm and respectful in resolving disagreements.</li>
              <li>Positive attitude and encouraging presence.</li>
              <li>Basic knowledge of soccer rules and flow.</li>
              <li>Responsible with equipment and field use.</li>
            </ul>
            <strong>Preferred (Not Required)</strong>
            <ul>
              <li>Leadership experience in sports or group settings.</li>
              <li>Basic first aid knowledge.</li>
              <li>Coaching or refereeing background.</li>
              <li>Active in the local soccer community.</li>
            </ul>
            <hr style={{ margin: '2rem 0' }} />
            <h4>Perks & Benefits</h4>
            <ul>
              <li>Free play at games you captain.</li>
              <li>Gain leadership and organization experience.</li>
              <li>Be recognized as a trusted leader in the soccer community.</li>
              <li>Network with players, captains, and organizers.</li>
              <li>Priority access to new games and updates.</li>
              <li>Potential rewards or credits for consistent hosting.</li>
            </ul>
            <button
              className="apply-btn"
              style={{ marginTop: '2rem', background: '#059669', color: '#fff', fontWeight: 600, border: 'none', borderRadius: '8px', padding: '0.9rem 2.2rem', fontSize: '1.1rem', cursor: 'pointer' }}
              onClick={() => setShowApplyForm((prev) => !prev)}
            >
              {showApplyForm ? 'Cancel' : 'Apply'}
            </button>
            {showApplyForm && (
              <form className="apply-form" onSubmit={handleApplySubmit} style={{ marginTop: '2rem', background: '#f8fafc', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(16,185,129,0.08)' }}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter your phone number"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="why">Why are you interested in being a Game Captain? *</label>
                  <textarea
                    id="why"
                    name="why"
                    value={formData.why}
                    onChange={handleChange}
                    required
                    placeholder="Tell us why you want to be a Game Captain..."
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="handleConflict">How would you handle it if players argue during a game? *</label>
                  <textarea
                    id="handleConflict"
                    name="handleConflict"
                    value={formData.handleConflict}
                    onChange={handleChange}
                    required
                    placeholder="Describe how you would handle player disagreements..."
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="fairTeams">How would you make sure teams are fair and balanced? *</label>
                  <textarea
                    id="fairTeams"
                    name="fairTeams"
                    value={formData.fairTeams}
                    onChange={handleChange}
                    required
                    placeholder="Describe your approach to team selection..."
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="gamesPerMonth">How many games per month are you able to commit to captaining? *</label>
                  <input
                    type="number"
                    id="gamesPerMonth"
                    name="gamesPerMonth"
                    value={formData.gamesPerMonth}
                    onChange={handleChange}
                    required
                    min="1"
                    placeholder="e.g. 2, 4, 8..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="coachingRefExp">Do you have any coaching, or refereeing experience? *</label>
                  <textarea
                    id="coachingRefExp"
                    name="coachingRefExp"
                    value={formData.coachingRefExp}
                    onChange={handleChange}
                    required
                    placeholder="Describe any coaching or refereeing experience..."
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="experience">Other Relevant Experience (optional)</label>
                  <textarea
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Share any leadership, soccer, or related experience..."
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="availability">Availability (days/times, locations) *</label>
                  <input
                    type="text"
                    id="availability"
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Weekends, weekday evenings, specific locations..."
                  />
                </div>
                {/* Status Messages */}
                {submitStatus === "success" && (
                  <div className="status-message success">
                    ✅ Application sent! We'll get back to you soon.
                  </div>
                )}
                {submitStatus === "error" && (
                  <div className="status-message error">
                    ❌ Failed to send application. Please try again or contact us directly.
                  </div>
                )}
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                  style={{ marginTop: '1rem', background: '#10b981', color: '#fff', fontWeight: 600, border: 'none', borderRadius: '8px', padding: '0.8rem 2rem', fontSize: '1rem', cursor: 'pointer' }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Careers;
